<script lang="ts" setup>
import { message } from 'ant-design-vue'
import { customSignUp, getOptionList, getSignUpConfig } from '~/server/common'
import { getAreaAndChildList, getNewHouseQueryByPage } from '~/server/newhouse'
import { useCityInfoStore } from '~/stores/useCityInfo'
import { getFloorIndexUrl } from '~/composables/useRouteAddress'
import { computedStatusColor } from '~/utils/utils-js'
import { useUserInfoStore } from '~/stores/useUserInfo'

interface messagesType {
  text: string
  sender: string
  avatar: string
  signUp?: boolean
  phone?: string
  disabled?: false
  ready?: boolean
  className?: string
  image?: string | null
};

/**
 * 是否显示ai找房
 */
const show = defineModel('show', { type: Boolean, default: false })

// 聊天框指向
const chatBox = ref()

const messages = ref<messagesType[]>([]) // 用户聊天记录
const showButton = ref(false) // "开始吧"按钮显隐
const showPriceOptions = ref(false) // 价格选择器
const showModelOptions = ref(false) // 模型选择器
const showAreaOptions = ref(false)
const showIntervalOptions = ref(false)
const showRestart = ref(false)
const showOtherFloor = ref(false)
const selectedPrices = ref([])
const selectedModel = ref([])
const selectedInterval = ref([])
const singlePrices = ref()
const room = ref()
const modelAreas = ref()
const areaIds = ref()
const zoneIds = ref()
const current = ref(1)
const pageSize = ref(4)

const selectedArea = ref<any>([])

const areaOptionsChild = ref<any>([])

const areaName = ref<any>([])

const configId = ref(0) // 报名配置ID

function handleAreaChoose(data: any, index?: any, name?: any) {
  if (index === 0) {
    // areaOptionsChild.value = data.childList.filter((item: any) => item.key)
    areaOptionsChild.value = data.childList
    areaName.value = []
    selectedArea.value = []
    areaName.value[index] = name
    selectedArea.value[index] = data.oldKey
  }
  else {
    areaName.value[index] = name
    selectedArea.value[index] = `1-${data.oldKey}`
  }
  data.oldKey && (areaIds.value = data.oldKey)
}

const { city } = useCityInfoStore()

async function fetchAreaChildList() {
  try {
    const areaChildList = await getAreaAndChildList({
      cityId: city, // 根据需要调整 cityId
    })

    // 可以在这里处理 areaChildList，例如更新状态或返回数据
    return areaChildList.dataBody.optionList
  }
  catch (error) {
    console.error('Error fetching area child list:', error)
    return []
  }
}

async function fetchNewHouseList() {
  try {
    const queryParams = {
      cityId: city,
      singlePrices: singlePrices.value,
      room: room.value,
      modelAreas: modelAreas.value,
      current: current.value,
      pageSize: pageSize.value,
      areaIds: areaIds.value,
      zoneIds: zoneIds.value,
    }
    /**
     * 去掉queryParams中的undefined或null的数据
     */
    Object.keys(queryParams).forEach((key: any) => {
      const queryParamsKey = key as keyof typeof queryParams
      if (queryParams[queryParamsKey] === undefined || queryParams[queryParamsKey] === null) {
        delete queryParams[queryParamsKey]
      }
    })
    const res = await getNewHouseQueryByPage(
      queryParams,
    )
    if (res.code === 0) {
      const { list } = res.dataBody
      if (list && list.length > 0) {
        let floors: any[] = []
        list.forEach((item: any) => {
          floors.push({
            text: '',
            image: item,
          })
        })
        // floors.push({
        //   signUp: true,
        // })
        if (list?.length > 0) {
          floors = [{ text: '根据您的需求，以下楼盘适合您~', image: null }, ...floors]
        }
        sendSequentialMessages(floors, 0)
      }
      else {
        addBotMessage('亲，暂未找到符合需求的楼盘，您可以点击下方按钮')
      }
    }
    else {
      addBotMessage('亲，暂未找到符合需求的楼盘，您可以点击下方按钮')
    }
    // 可以在这里处理 areaChildList，例如更新状态或返回数据
    return res.dataBody
  }
  catch (error) {
    console.error('Error fetching area child list:', error)
    return []
  }
}

function showModal() {
  show.value = true
}

function onClose() {
  show.value = false
}

defineExpose({
  showModal,
  onClose,
})

/**
 * 小程序/M版/PC端 价格筛选-单价
 */
const { data: priceOptions } = await useAsyncData(() => getOptionList({ optionTypeCode: 'appFloorSinglePrice', cityId: city }), {
  transform: (res: any) => {
    const { dataBody } = res
    dataBody.push({ optionKey: -1, optionValue: '都可以' })
    return dataBody
  },
})
// 修改单位为千和万
function formatValue(value: any) {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`
  }
  else {
    return `${(value / 1000).toFixed(0)}千`
  }
}

priceOptions.value.forEach((item: any) => {
  if (item.optionValue.includes('元')) {
    const low = item.valueLow ? Number.parseFloat(item.valueLow) : null
    const high = item.valueHigh ? Number.parseFloat(item.valueHigh) : null

    if (high && low) {
      // 修改范围内的单位
      item.optionValue = `${formatValue(low)}-${formatValue(high)}`
    }
    else if (low && high === null) {
      // 处理如 "15000元以上" 的情况
      item.optionValue = `${formatValue(low)}以上`
    }
    else if ([0, null].includes(low) && high) {
      // 处理如 "15000元以下" 的情况
      item.optionValue = `${formatValue(high)}以下`
    }
  }
  return item
})

/**
 * app首页新房户型
 */
const { data: modelOptions } = await useAsyncData(() => getOptionList({ optionTypeCode: 'appFloorHouseModel', cityId: city }), {
  transform: (res: any) => {
    const { dataBody } = res
    dataBody.push({ optionKey: -1, optionValue: '都可以' })
    return dataBody
  },
})

/**
 * 小程序/M版/PC端 更多筛选-交房时间
 */
const { data: intervalOptions } = await useAsyncData(() => getOptionList({ optionTypeCode: 'appFloorModelArea', cityId: city }), {
  transform: (res: any) => {
    const { dataBody } = res
    dataBody.push({ optionKey: -1, optionValue: '都可以' })
    return dataBody
  },
})

const { data: areaOptions } = await useAsyncData(() => fetchAreaChildList(), {
  transform: (res: any) => {
    return res
  },
})

onMounted(async () => {
  // await fetchOptions()
  animateMessages()
})

function animateMessages() {
  sendSequentialMessages([
    { text: 'HI,我是房超AI找房小助手' },
    { text: '可以和我聊聊您的购房需求，我将为您推荐匹配适合的楼盘，如果需要请点击下方按钮~' },
  ], 0)
}

function sendSequentialMessages(messages: any, index: any) {
  if (index < messages.length) {
    const message = messages[index]
    if (message.text === '已了解您的购房需求，根据要求在努力查找楼盘~') {
      addBotMessage(message.text, message.image, (): any => {
        fetchNewHouseList()
      })
    }
    // else if (message.SignUp) {
    //   addBotMessage('报名')
    // }
    else {
      addBotMessage(message.text, message.image, () => {
        sendSequentialMessages(messages, index + 1)
      })
      if (message.image && ((index + 1) === messages.length)) {
        showOtherFloor.value = true
      }
    }
  }
}

/**
 * 为机器人添加回复信息
 * 处理打字效果
 * @param text
 * @param image
 * @param callback
 */
function addBotMessage(text: any, image: any = null, callback: any = null) {
  // 初始的占位符消息
  let placeholderMessage = {
    text: '...',
    sender: 'bot',
    avatar: 'bot-avatar.jpg',
    className: 'blinking-placeholder',
    image: null,
    // SignUp: false,
    // phone: '',
    // disabled: false,
    // ready: false,
  }

  if (text === '报名') {
    const { info } = useUserInfoStore()
    text = null
    placeholderMessage = {
      signUp: true,
      sender: 'bot',
      phone: info?.mobile,
      disabled: false,
      ready: false,
    }
    showRestart.value = true
    showButton.value = false
    showOtherFloor.value = false
  }

  // 添加占位符消息到消息列表
  messages.value.push(placeholderMessage)

  // 模拟网络请求的延迟
  setTimeout(() => {
    if (image) {
      // 如果有图片，直接显示完整文本和图片
      const lastMessageIndex = messages.value.length - 1
      messages.value[lastMessageIndex] = {
        ...messages.value[lastMessageIndex],
        text,
        image,
      }
      updateStateBasedOnText(text)
      if (callback)
        callback()
    }
    else {
      // 文本动画的延迟时间
      const delay = 10
      let currentLength = 0

      const animateText = () => {
        if (currentLength >= text?.length) {
          const lastMessageIndex = messages.value.length - 1
          messages.value[lastMessageIndex] = {
            ...messages.value[lastMessageIndex],
            text,
            image,
          }
          updateStateBasedOnText(text)
          if (callback)
            callback()
          return
        }

        currentLength++
        const lastMessageIndex = messages.value.length - 1
        messages.value[lastMessageIndex] = {
          ...messages.value[lastMessageIndex],
          text: text.slice(0, currentLength),
          image,
        }
        // .chat-box滚动到底部
        nextTick(() => {
          if (chatBox.value)
            chatBox.value.scrollTop = chatBox.value.scrollHeight
        })

        setTimeout(animateText, delay)
      }

      animateText()
    }
  }, 1000) // 1秒后执行文本动画
}

function handleSendMessage() {
  addUserMessage('智能找房', 1)
  showButton.value = false // "开始吧"按钮显隐
}

/**
 * 添加用户消息
 * 计算机器人返回信息
 * @param text
 * @param step
 */
function addUserMessage(text: any, step: any) {
  const newMessage = { text, sender: 'user', avatar: 'user-avatar.jpg' }
  messages.value.push(newMessage)

  const messageInfo: any = {
    1: '我很乐意帮你找到合适的楼盘，请选择预期单价。',
    2: '了解，您对楼盘地段有什么想法？',
    3: '亲，您对户型有什么要求么？',
    4: '嗯嗯，您对面积段有什么要求吗？',
    5: [{ text: '已了解您的购房需求，根据要求在努力查找楼盘~' }, { text: '根据您的需求，以下楼盘适合您~2' }],
    6: '我很乐意帮你找到合适的楼盘，请选择预期单价。',
    7: [{ text: '根据您的需求，以下楼盘适合您~3' }],
  }
  if ([1, 2, 3, 4, 6].includes(step)) {
    setTimeout(() => {
      addBotMessage(messageInfo[step])
      // if (step === 2) {
      //   setTimeout(() => {
      //     addBotMessage('您选好区域板块，请点击确认按钮。')
      //   }, 1000)
      // }
    }, 1000)
  }
  else if ([5, 7].includes(step)) {
    if (step === 7)
      current.value = current.value + 1
    sendSequentialMessages(messageInfo[step], 0)
  }
}

/**
 * 根据机器人返回的文本，更新状态
 * @param text
 */
function updateStateBasedOnText(text: any) {
  if (text.includes('如果需要请点击下方按钮')) {
    showButton.value = true // "开始吧"按钮显隐
  }
  else if (text.includes('请选择预期单价')) {
    setTimeout(() => {
      showPriceOptions.value = true
    }, 1000)
  }
  else if (text.includes('您对楼盘地段有什么想法')) {
    setTimeout(() => {
      showAreaOptions.value = true
      showPriceOptions.value = false
    }, 1000)
  }
  else if (text.includes('您对户型有什么要求么')) {
    setTimeout(() => {
      showModelOptions.value = true
      showAreaOptions.value = false
    }, 1000)
  }
  else if (text.includes('您对面积段有什么要求吗')) {
    setTimeout(() => {
      showIntervalOptions.value = true
      showModelOptions.value = false
    }, 1000)
  }
  else if (text.includes('暂未找到符合需求的楼盘')) {
    showRestart.value = true
    showIntervalOptions.value = false
  }
  else if (text === 'end') {
    showOtherFloor.value = true
    // .chat-box滚动到底部
    if (chatBox.value)
      chatBox.value.scrollTop = chatBox.value.scrollHeight
  }
}

/**
 * 选择价格
 */
function handlePriceOptionClick(price: any) {
  if (price.optionKey !== -1) {
    selectedPrices.value = [price]
    singlePrices.value = price.optionKey
  }
  addUserMessage(price.optionValue, 2)
  showPriceOptions.value = false
}

/**
 * 选择户型
 * @param model
 */
function handleModelOptionClick(model: any) {
  if (model) {
    if (model.optionKey !== -1) {
      selectedModel.value = [model]
      room.value = model.optionKey
    }
    addUserMessage(model.optionValue, 4)
  }
  else {
    sendSequentialMessages([
      { text: '已了解您的购房需求，根据要求在努力查找楼盘~' },
      { text: '根据您的需求，以下楼盘适合您~4' },
    ], 0)
  }
  showModelOptions.value = false
}

/**
 * 选择面积
 * @param interval
 */
function handleIntervalOptionClick(interval: any) {
  if (interval) {
    if (interval.optionKey !== -1) {
      selectedInterval.value = [interval]
      modelAreas.value = interval.optionKey
    }
    addUserMessage(interval.optionValue, 5)
  }
  else {
    sendSequentialMessages([
      { text: '已了解您的购房需求，根据要求在努力查找楼盘~' },
      { text: '根据您的需求，以下楼盘适合您~5' },
    ], 0)
  }
  showIntervalOptions.value = false
}

/**
 * 重新开始
 */
function onRestart() {
  showRestart.value = false
  showOtherFloor.value = false
  singlePrices.value = undefined
  room.value = undefined
  modelAreas.value = undefined
  areaIds.value = undefined
  zoneIds.value = undefined
  current.value = 1
  pageSize.value = 4
  selectedPrices.value = []
  selectedModel.value = []
  selectedInterval.value = []
  areaName.value = []
  selectedArea.value = []
  areaIds.value = null
  addUserMessage('重新开始吧', 6)
}

/**
 * 报名
 */
const router = useRouter()
function showOtherFloors() {
  if (useCookie('AiFind').value)
    window.location.href = '/newhouse/'

  else addBotMessage('报名')
}

/**
 * 选择区域
 * @param selectedOptions
 */
function onConfirm() {
  if (areaIds.value) {
    addUserMessage(areaName.value.join('-'), 3)
  }
  else {
    areaIds.value = null
    // addBotMessage('您对户型有什么要求么')
    addUserMessage('都可以', 3)
    // addBotMessage(`请选择区域`)
  }
  if (areaIds.value)
    showAreaOptions.value = false
}

function onConfirmAllArea() {
  areaIds.value = null
  sendSequentialMessages([
    { text: '已了解您的购房需求，根据要求在努力查找楼盘~' },
    // { text: '根据您的需求，以下楼盘适合您~6' },
  ], 0)
  showAreaOptions.value = false
}

/**
 * 提交报名
 */
function submitSignUp(data: any) {
  // 正则校验手机号data.phone
  if (!/^1[3-9]\d{9}$/.test(data.phone)) {
    message.error('请输入正确的手机号')
    return
  }
  // 检查有没有勾选data.ready
  if (!data.ready) {
    message.error('请阅读并同意《新房楼盘AI找房服务协议》')
    return
  }
  const params = {
    businessId: configId.value,
    cityId: city,
    mobile: data?.phone,
    source: '新房楼盘AI找房',
    sourceTypeId: 1,
  }
  customSignUp(params).then((res) => {
    const { code, message: msg } = res
    if (code === 0) {
      message.success(msg)
      // 提示
      data.disabled = true
      useCookie('AiFind').value = 'true'
    }
    else {
      // 提示
      message.error(msg)
    }
  })
}

/**
 * 获取报名配置信息
 */
getSignUpConfig({ configCodes: 'mBrandHouseSearchFloorList' }).then((res) => {
  const { code, dataBody } = res
  if (code === 0)
    configId.value = dataBody[0]?.configId
})

// watch(() => messages.value, (newval) => {
//   console.log(newval, 1234566)
//   // .chat-box滚动到底部
//   chatBox.value.scrollTop = chatBox.value.scrollHeight
// })

/**
 * 用于计算楼盘的tag颜色
 */
function getColor(info: any) {
  return computedStatusColor(info)
}
</script>

<template>
  <div v-if="show" class="desk">
    <div class="container">
      <div class="title">
        智能找房
      </div>
      <CloseOutlined class="close-btn" @click="show = false" />
      <div ref="chatBox" class="chat-box">
        <div class="chat-container">
          <!--            聊天记录 -->
          <div
            v-for="(message, index) in messages" :key="index"
            :class="message.sender === 'bot' ? 'bot-message' : 'user-message'"
          >
            <img
              v-if="message.sender === 'bot'" src="@/assets/images/AIavatar.gif"
              class="avatar"
            >
            <!--            跳转详情★★★wzy -->
            <div v-if="message.image" class="message-image-container">
              <a target="_blank" :href="getFloorIndexUrl(message.image.floorId)">
                <!-- 图片消息的布局 -->
                <div class="image-box">
                  <img :src="$getImageSize(message.image.photo, 'c460x190')" class="message-image">
                  <div v-if="message.image.openingDate" class="message-time">
                    {{ message.image.openingDate }}
                  </div>
                </div>
                <div class="message-box">
                  <div class="message-floor">
                    <div class="message-title">
                      {{ message.image.floor }}
                    </div>
                    <!--                    <img src="https://imgf1.fccs.com.cn/newhouse-mobile/ai/collect.png" class="collect"> -->
                    <like :is-collect="message.image?.isFavorite" :data="message.image" :title="message?.floor" :number-id="message.image?.floorId" :text="['', '']" />
                  </div>
                  <div class="price-box">
                    <span class="message-price">参考价：
                      <small>{{ message.image.pricePrefix }}</small>{{ message.image.price || message.image.frontDisplayPrice }}<small>{{ message.image.priceSuffix }}</small>
                      <!--                      {{ message.image.priceDisplay }} -->
                    </span>
                    <!--                    <span class="message-price">参考价：{{ message.image.priceDisplay }}</span> -->
                  <!--                <span class="message-unit">元/㎡</span> -->
                  </div>
                  <div class="message-info">
                    <span class="message-area">{{ message.image.cityName }}-{{ message.image.areaName }}</span>
                    <tag v-for="(item, index) in (message.image.saleStatusIdList || [])" :key="index" :color="getColor({ saleStatusId: item })">
                      {{ message.image.saleStatusList ? message.image.saleStatusList[index] : '' }}
                    </tag>
                    <tag v-for="(item, index) in (message.image.propertyType || '').split(',').slice(0, 2)" :key="index" :color="getColor(message.image)">
                      {{ item }}
                    </tag>
                    <!--                    <span class="sale-status">{{ message.image.saleStatus || '待售' }}</span> -->
                    <template v-if="message.image.floorLabel?.splice(0, 2)">
                      <span
                        v-for="(item, index) in message.image.floorLabel.split(',')" :key="index"
                        class="message-label"
                      >{{ item }}</span>
                    </template>
                  </div>
                  <div v-if="message.image.rank" class="message-bottom">
                    <span class="message-text">榜</span>
                    <span class="message-top">{{ message.image.rank }}</span>
                  </div>
                </div>
              </a>
            </div>
            <div v-if="message.signUp" class="sign-up-box">
              <div class="sign-up-title">
                已为您匹配合适的楼盘，请留下联系方式
              </div>
              <div class="phone">
                手机号码 <input v-model="message.phone" :disabled="message.disabled" type="text" class="phone-input">
              </div>
              <div class="agreement">
                <a-checkbox v-model:checked="message.ready" :disabled="message.disabled">
                  我已阅读并同意 <a href="https://corp.fccs.com/fccs.html?id=5">《房产超市网用户协议》</a>
                </a-checkbox>
              </div>
              <div :class="`apply-bottom ${message.disabled ? 'disabled' : ''}`" @click="submitSignUp(message)">
                {{ message.disabled ? '已申请' : '申请查看匹配楼盘' }}
              </div>
              <div class="tip">
                申请后会有专业顾问致电联系并提供服务
              </div>
            </div>
            <div
              v-else-if="message.text"
              :class="[message.sender === 'bot' ? 'bot-text' : 'user-text', message.text === '...' ? 'blinking-placeholder' : '']"
            >
              {{ message.text }}
            </div>
          </div>
        </div>
      </div>
      <!--       价格选项的渲染 -->
      <div v-if="showPriceOptions" class="price-options">
        <div
          v-for="(price, index) in priceOptions"
          :key="index"
          :class="`price ${selectedPrices.includes(price) ? 'selected-price' : ''}`"
          @click="handlePriceOptionClick(price)"
        >
          {{ price.optionValue }}
        </div>
      </div>
      <!-- 模型选项的渲染 -->
      <div v-if="showModelOptions" class="price-options">
        <div
          v-for="(model, index) in modelOptions"
          :key="index"
          :class="`price ${selectedModel.includes(model) ? 'selected-price' : ''}`"
          @click="handleModelOptionClick(model)"
        >
          {{ model.optionValue }}
        </div>
        <div class="clear" style="width: 100%" />
        <button class="send-button" @click="handleModelOptionClick(null)">
          不想选了
        </button>
      </div>
      <!-- 面积选项的渲染 -->
      <div v-if="showIntervalOptions" class="price-options">
        <div
          v-for="(interval, index) in intervalOptions" :key="index"
          :class="`price ${selectedInterval.includes(interval) ? 'selected-price' : ''}`"
          @click="handleIntervalOptionClick(interval)"
        >
          {{ interval.optionValue }}
        </div>
        <div class="clear" style="width: 100%" />
        <button class="send-button" @click="handleIntervalOptionClick(null)">
          不想选了
        </button>
      </div>
      <!--            区域选择 -->
      <!--          <div class="area-choose"> -->
      <div v-if="showAreaOptions" class="price-options">
        <div class="operator-bottom">
          区域

          <span class="area-confirm" @click="onConfirm">
            确认
          </span>
        </div>
        <div
          v-for="(item, index) in areaOptions"
          :key="index"
          :class="`price ${selectedArea.includes(item.oldKey) ? 'selected-price' : ''}`"
          @click="handleAreaChoose(item, 0, item.value)"
        >
          <!--          @click="handlePriceOptionClick(price)" -->
          {{ item?.value }}
        </div>
        <div class="clear" style="width: 100%" />
        <div class="area-name">
          {{ areaName.join('-') }}
        </div>
        <div
          v-for="(item, index) in areaOptionsChild"
          :key="index"
          :class="`price ${selectedArea.includes(`1-${item.oldKey}`) ? 'selected-price' : ''}`"
          @click="handleAreaChoose(item, 1, item.value)"
        >
          <!--          @click="handlePriceOptionClick(price)" -->
          {{ item?.value }}
        </div>
        <div class="clear" style="width: 100%" />
        <button class="send-button" @click="onConfirmAllArea">
          不想选了
        </button>
      </div>
      <!--          </div> -->
      <!--            各种按钮 -->
      <div class="input-container">
        <button v-if="showButton" class="send-button" @click="handleSendMessage">
          开始吧
        </button>
        <button v-if="showRestart" class="send-button" @click="onRestart">
          重新开始吧
        </button>
        <button v-if="showOtherFloor" class="send-button" @click="showOtherFloors">
          <!--          换一批房吧 -->
          查看更多
        </button>
        <button v-if="showOtherFloor" class="send-button" @click="onRestart">
          重新开始吧
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
a {
  &:hover {
    color: unset;
  }
}
.tag {
  padding: 0 3px;
  margin-right: 4px;
}

.desk {
  position: fixed;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: #00000099;
  z-index: 100;
}

.container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(180deg, #F7FDFF 0%, #F0FAF9 100%);
  //padding-top: 40px;
  padding-top: 90px;
  //max-height: 900px;
  height: 700px;
  overflow-y: auto;
  transition: all 0.5s;
  background-image: url("~/assets/images/AI-Back.jpg");
  background-size: 100% auto;

  .title {
    position: absolute;
    top: 30px;
    left: 0;
    right: 0;
    text-align: center;
    font-family: Alibaba PuHuiTi 3.0;
    font-size: 30px;
    font-weight: bold;
    color: #3D3D3D;
  }

  .close-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
    color: #999999;
    font-size: 20px;
  }
}

.chat-box {
  height: 535px;
  overflow: hidden;
  overflow-y: auto;
}

.bot-message {
  display: flex;
  align-items: flex-start;
  margin-bottom: 22px;
}

.user-message {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  margin-bottom: 22px;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin: 0 25px
}

.bot-text {
  font-size: 16px;
  max-width: calc(100% - 120px);
  word-wrap: break-word;
  padding: 10px;
  background: #FFFFFF;
  color: #3D3D3D;
  border-radius: 10px;
}

.user-text {
  font-size: 16px;
  max-width: calc(100% - 120px);
  word-wrap: break-word;
  padding: 10px;
  border-radius: 10px 0 10px 10px;
  background: linear-gradient(270deg, #1DA838 38%, #5CC777 100%);
  color: #FFFFFF;
  margin-right: 25px;
}

.input-container {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
  background: linear-gradient(180deg, #EBF5FF 0%, rgba(255, 255, 255, 0.9) 100%);
  position: absolute;
  bottom: 0;
  box-shadow: 0px 4px 10px 0px rgb(144 144 144);
}

.send-button {
  color: white;
  border-radius: 50px;
  height: 46px;
  line-height: 46px;
  text-align: center;
  //padding: 0 66px;
  border: unset;
  cursor: pointer;
  margin: 18px auto;
}

.send-button:only-child {
  background: linear-gradient(270deg, #1DA838 38%, #5CC777 100%); /* 单个按钮时的背景颜色 */
  padding: 0 175px;
}

.send-button:first-child:not(:only-child) {
  background: linear-gradient(270deg, #F66814 32%, #F08B56 100%, #35BD5B 100%); /* 两个按钮时第一个按钮的背景颜色 */
}

.send-button:last-child:not(:only-child) {
  background: linear-gradient(270deg, #1DA838 38%, #5CC777 100%); /* 两个按钮时第二个按钮的背景颜色 */
}

.send-button:not(:only-child) {
  padding: 0 66px;
}

.message-image-container {
  display: flex;
  flex-flow: column;
  border-radius: 10px;
  margin-left: 10px;
  background-color: #FFFFFF;
  margin-bottom: 20px;

  .image-box {
    position: relative;

    .message-image {
      border-radius: 10px 10px 0 0;
      width: 231px;
      height: 94px;
      object-fit: cover;
      opacity: 1;
    }

    .message-time {
      background: linear-gradient(270deg, #F66814 32%, #F08B56 100%, #35BD5B 100%);
      color: #FFFFFF;
      border-radius: 5px;
      font-size: 13px;
      font-weight: bold;
      position: absolute;
      top: 8px;
      left: 8px;
      padding: 3px 6px;
      line-height: 16px;
    }
  }

  .message-box {
    padding: 10px;

    .message-floor {
      display: flex;
      flex-flow: row;
      align-items: center;
      margin-bottom: 10px;
      border-radius: 0 0 10px 10px;

      .message-title {
        font-size: 16px;
        font-weight: bold;
        flex: 1;
      }

      .collect {
        height: 15px;
        width: 15px;
      }
    }

    .price-box {
      display: flex;
      margin-bottom: 12px;
      flex-flow: row;
      align-items: center;

      .message-price {
        font-size: 16px;
        font-weight: 700;
        color: #FF5B05;
      }
    }

    .message-info {
      display: flex;
      flex-flow: row;
      align-items: center;
      margin-bottom: 10px;
      width: 210px;
      @include ell();

      .message-area {
        font-size: 12px;
        margin-right: 12px;
      }

      .sale-status {
        color: #448BE9;
        font-size: 12px;
        padding: 1px 6px;
        background: rgba(68, 139, 233, 0.1);
        line-height: 16px;
        margin-right: 12px;
        border-radius: 5px;
      }

      .message-label {
        font-size: 24px;
        color: #888888;
        padding: 6px;
        background: #F9F9F9;
      }
    }

    .message-bottom {
      display: flex;
      flex-flow: row;
      align-items: center;

      .message-text {
        background: linear-gradient(135deg, #D19B59 0%, #F6CB96 100%);
        font-size: 12px;
        color: #FFFFFF;
        font-weight: bold;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        padding: 0 4px;
        height: 15px;
        line-height: 15px;
      }

      .message-top {
        font-size: 12px;
        color: #9E6F36;
        background: #F6EFE3;
        height: 15px;
        line-height: 15px;
        padding: 0 8px;
        border-radius: 2px;
      }
    }
  }
}

.sign-up-box {
  width: 327px;
  background: linear-gradient(180deg, #EAF2F7 0%, #ECEDF1 100%);
  border: 0.5px solid #E1E1E1;
  box-sizing: border-box;
  padding: 15px;
  border-radius: 0px 10px 10px 10px;
  margin-bottom: 50px;

  .sign-up-title {
    color: #3D3D3D;
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 10px;
  }

  .phone {
    color: #3D3D3D;
    font-size: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    .phone-input {
      width: 219px;
      height: 40px;
      border-radius: 5px;
      background: #FFFFFF;
      border: 0.5px solid #E1E1E1;
      padding: 12px;
      font-size: 16px;
      color: #9C9C9C;
    }
  }

  .agreement {
    font-size: 14px;
    color: #3D3D3D;
    margin-bottom: 10px;

    a {
      color: #1DA838;
    }
  }

  .apply-bottom {
    width: 297px;
    height: 40px;
    line-height: 40px;
    border-radius: 50px;
    background: linear-gradient(270deg, #F66814 32%, #F08B56 100%, #35BD5B 100%);
    margin: 0 auto 10px;
    text-align: center;
    font-size: 16px;
    font-weight: bold;
    color: #FFFFFF;
    cursor: pointer;
  }

  .disabled {
    background: #9C9C9C;
  }

  .tip {
    font-size: 14px;
    color: #8B8A8A;
    text-align: center;
  }
}

.message-unit {
  font-size: 26px;
  font-weight: 400;
  color: #FF5B05;
  line-height: 22px;
}

.price-options {
  //position: fixed;
  //bottom: 0;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  background: #FFFFFF;
  padding: 20px;
  //justify-content: space-between;
  row-gap: 10px;
  column-gap: 13px;
  box-sizing: border-box;
  position: absolute;
  bottom: 0;
  box-shadow: 0px 4px 10px 0px rgb(144 144 144);

  .operator-bottom {
    width: 100%;
    margin-bottom: 10px;
    line-height: 37px;

    .area-confirm {
      font-size: 16px;
      float: right;
      padding: 8px 17px;
      color: #FFFFFF;
      background: linear-gradient(270deg, #1DA838 38%, #5CC777 100%);
      border-radius: 50px;
      line-height: 16px;
      cursor: pointer;
    }
  }

  .area-name {
    width: 100%;
    font-size: 16px;
    line-height: 30px;
  }

  .price {
    color: #3D3D3D;
    border-radius: 5px;
    //height: 44px;
    //line-height: 44px;
    text-align: center;
    background-color: #F2F7F9;
    //flex: 0 0 calc(25% - 10px);
    flex: 0 0 calc(20% - 11px);
    //margin-right: 10px;
    //margin-bottom: 10px;
    font-size: 16px;
    cursor: pointer;
    //padding: 0 5px;
    padding: 6px 0px;

    @include ell();
  }
}

.area-choose {
  position: fixed;
  bottom: 0;
  width: 100%;
  display: flex;
  padding: 15px 0;
  flex-flow: column;
  background: #FFFFFF;
}

.area-top {
  padding: 8px 20px;
  display: flex;
  justify-content: space-between;
}

.picker-view-column {
  height: 80% !important;
}

.area-right {
  color: #1DA838;
  font-weight: bold;
}

.area-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.selected-price {
  color: #1DA838 !important;
  font-size: 16px;
  font-weight: bold;
  border: 1px solid #1DA838;
  background: linear-gradient(180deg, rgba(209, 250, 223, 0) 0%, #F3FAF4 100%);
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

.blinking-placeholder {
  animation: blink 1s linear infinite;
}
</style>
